import clsx from "clsx";
import { Tooltip as TooltipPrimitive } from "radix-ui";
import React from "react";
import styles from "./tooltip.module.scss";

// --- Provider Component (should wrap the app or section using tooltips) ---
const TooltipProvider = TooltipPrimitive.Provider;

// --- Root Component (contains Trigger and Content) ---
const Tooltip = TooltipPrimitive.Root;

// --- Trigger Component (element that triggers the tooltip on hover/focus) ---
const TooltipTrigger = TooltipPrimitive.Trigger;

// --- Portal Component (renders content into document.body or specified container) ---
const TooltipPortal = TooltipPrimitive.Portal;

// --- Content Component (the tooltip popup) ---
export type TooltipContentProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Content
> & {
  /** Offset from the trigger element */
  sideOffset?: number;
  /** Prevent tooltip from colliding with viewport edges */
  avoidCollisions?: boolean;
  /** Padding when avoiding collisions */
  collisionPadding?: number;
  /** Smart positioning for toolbar elements */
  forToolbar?: boolean;
};

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(
  (
    {
      className,
      sideOffset = 8, // Increased default offset for better visibility
      avoidCollisions = true,
      collisionPadding = 8,
      forToolbar = false,
      side,
      children,
      ...props
    },
    ref
  ) => {
    // Smart positioning for toolbar elements
    const smartSide = forToolbar && !side ? "bottom" : side;

    return (
      <TooltipPortal>
        <TooltipPrimitive.Content
          ref={ref}
          side={smartSide}
          sideOffset={sideOffset}
          avoidCollisions={avoidCollisions}
          collisionPadding={collisionPadding}
          className={clsx(styles.content, className)}
          {...props}
        >
          {children}
          <TooltipArrow />
        </TooltipPrimitive.Content>
      </TooltipPortal>
    );
  }
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// --- Arrow Component (optional visual arrow) ---
export type TooltipArrowProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Arrow
>;
const TooltipArrow = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Arrow>,
  TooltipArrowProps
>(({ className, ...props }, ref) => (
  <TooltipPrimitive.Arrow
    ref={ref}
    className={clsx(styles.arrow, className)}
    {...props}
  />
));
TooltipArrow.displayName = TooltipPrimitive.Arrow.displayName;

export {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipPortal,
  TooltipContent,
  TooltipArrow,
};
